import React from 'react';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import styles from './CheckoutProgressBar.module.scss';
import messages from './messages';
import {
    DELIVERY_METHODS_STEP,
    SHIPPING_INFO_STEP,
    PAYMENT_STEP,
    REVIEW_STEP
} from '../../blocks/Checkout/SteppedCheckout/constants';

type CheckoutProgressBarProps = {
    currentStep: string;
    storeTypeId: string;
    isDraftCart: boolean;
    checkStepIsComplete: (params: { step: string }) => boolean;
    onClickEdit: (params: { step: string }) => void;
    editingStep: string;
}

const CheckoutProgressBar =
    ({
        currentStep,
        isDraftCart,
        storeTypeId,
        checkStepIsComplete,
        onClickEdit,
        editingStep
    }: CheckoutProgressBarProps) => {
    const { formatMessage } = useIntl();

        const hideEditLink = isDraftCart && storeTypeId;

        const isShippingCompleted = checkStepIsComplete({ step: SHIPPING_INFO_STEP });
        const isShippingEditing = editingStep === SHIPPING_INFO_STEP;

        const isDeliveryCompleted = checkStepIsComplete({ step: DELIVERY_METHODS_STEP });
        const isDeliveryEditing = editingStep === DELIVERY_METHODS_STEP;

        const isPaymentCompleted = checkStepIsComplete({ step: PAYMENT_STEP });
        const isPaymentEditing = editingStep === PAYMENT_STEP;

    return (
        <div className={styles['c-progress-bar-container']}>
            <div className={classnames(styles['c-progress-bar'], {
                [styles.focused]: [
                    SHIPPING_INFO_STEP,
                    DELIVERY_METHODS_STEP,
                    PAYMENT_STEP,
                    REVIEW_STEP
                ].includes(currentStep)
                })}
                role='button'
                onClick={() =>
                    (
                        isShippingCompleted &&
                        !isShippingEditing
                    ) ? onClickEdit({ step: SHIPPING_INFO_STEP }) : false
                }
                tabIndex={0}
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        isShippingCompleted &&
                        !isShippingEditing &&
                        !hideEditLink
                    ) {
                        onClickEdit({ step: SHIPPING_INFO_STEP });
                    }
                }}
            >
                <div
                    className={classnames(styles.title, {
                        [styles['current-step']]:
                            SHIPPING_INFO_STEP === currentStep
                    })}
                >
                    {formatMessage(messages.step1)}
                </div>
                <div className={classnames({
                    [styles['progress-bar']]: true,
                    [styles['border-radius-left']]: true
                })} />
            </div>
            <div className={classnames(styles['c-progress-bar'], {
                [styles.focused]:
                    [
                    DELIVERY_METHODS_STEP,
                    PAYMENT_STEP,
                    REVIEW_STEP
                ].includes(currentStep)
                })}
                role='button'
                onClick={() =>
                    (
                        isDeliveryCompleted &&
                        !isDeliveryEditing &&
                        !hideEditLink
                    ) ? onClickEdit({ step: DELIVERY_METHODS_STEP }) : false
                }
                tabIndex={0}
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        isDeliveryCompleted &&
                        !isDeliveryEditing &&
                        !hideEditLink
                    ) {
                        onClickEdit({ step: DELIVERY_METHODS_STEP });
                    }
                }}
            >
                <div
                    className={classnames(styles.title, {
                        [styles['current-step']]:
                            DELIVERY_METHODS_STEP === currentStep
                    })}
                >
                    {formatMessage(messages.step2)}
                </div>
                <div className={classnames({
                    [styles['progress-bar']]: true,
                    [styles['border-radius-none']]: true
                })} />
            </div>
            <div className={classnames(styles['c-progress-bar'], {
                [styles.focused]:
                    currentStep === PAYMENT_STEP || currentStep === REVIEW_STEP
                })}
                role='button'
                onClick={() =>
                    (
                        isPaymentCompleted &&
                        !isPaymentEditing &&
                        !hideEditLink
                    ) ? onClickEdit({ step: PAYMENT_STEP }) : false
                }
                tabIndex={0}
                onKeyDown={(e) => {
                    if (
                        e.key === 'Enter' &&
                        isPaymentCompleted &&
                        !isPaymentEditing &&
                        !hideEditLink
                    ) {
                        onClickEdit({ step: PAYMENT_STEP });
                    }
                }}
            >
                <div
                    className={classnames(styles.title, {
                        [styles['current-step']]:
                            PAYMENT_STEP === currentStep
                    })}
                >
                    {formatMessage(messages.step3)}
                </div>
                <div className={classnames({
                    [styles['progress-bar']]: true,
                    [styles['border-radius-right']]: true
                })} />
            </div>
        </div>
    );
}

export default CheckoutProgressBar;
